"use client";

import clsx from "clsx";
import React from "react";
import {useTranslation} from "react-i18next";
import type {MessageContentBuildWorkflow} from "@/services/wingman";
import Icon, {ICON_SIZES} from "../library/Icon";
import FileBubbleFrame from "./ask-file-bubble/FileBubbleFrame";
import AfterCallHandoffFooter from "./workflow-build-handoff/AfterCallHandoffFooter";
import ToolHandoffFooter from "./workflow-build-handoff/ToolHandoffFooter";
import {
   useWorkflowBuildHandoff,
   type BuildPublishedHandler,
   type HandoffStatus,
} from "./workflow-build-handoff/useWorkflowBuildHandoff";

/**
 * Chat card for a workflow-builder handoff emitted by the assistant's
 * `build_workflow` tool. All state and side effects live in
 * `useWorkflowBuildHandoff`; this component only renders.
 *
 * `purpose: "after_call"` builds in the background: once the assistant turn has
 * finished streaming, the card creates a draft, runs the builder brain
 * headlessly, publishes, subscribes the Gail webhook, and then tells the chat
 * to continue (`onBuildPublished`). The operator never leaves the conversation.
 *
 * `purpose: "tool"` keeps the explicit button: on click it creates a draft,
 * stashes the build prompt as a one-shot seed, and navigates to the builder,
 * which builds, publishes, and attaches the tool before returning to GailGPT.
 *
 * Either way the created workflow is linked to this `callId` (localStorage) so
 * a duplicate is never created and a revisit offers "Open workflow".
 */
const AskWorkflowBuilderBubble: React.FC<{
   isUser?: boolean;
   agentName?: string;
   brandOverride?: string;
   className?: string;
   buildWorkflow: MessageContentBuildWorkflow;
   isLastInGroup?: boolean;
   /** Whether the assistant turn has finished streaming; gates the button and the auto-start. */
   canApprove?: boolean;
   /**
    * Whether the chat can take the continuation message right now (idle, and
    * this card still ends the conversation). While false the message stays
    * queued; see `useWorkflowBuildHandoff`.
    */
   canSendContinuation?: boolean;
   hideAgentHeader?: boolean;
   /** Assistant this handoff came from; the tool attaches to and returns to it. */
   originAssistantId?: string;
   /** Sends the continuation message once an after-call build has published. */
   onBuildPublished?: BuildPublishedHandler;
}> = ({
   isUser,
   agentName,
   brandOverride,
   className,
   buildWorkflow,
   isLastInGroup = true,
   canApprove = false,
   canSendContinuation = false,
   hideAgentHeader = false,
   originAssistantId,
   onBuildPublished,
}) => {
   const {t} = useTranslation("ask");
   const {
      status,
      failure,
      linkedWorkflowId,
      alreadyPublished,
      isInProgress,
      openWorkflow,
      startBackgroundBuild,
      startBuilderHandoff,
   } = useWorkflowBuildHandoff({
      buildWorkflow,
      isUser,
      canApprove,
      canSendContinuation,
      originAssistantId,
      onBuildPublished,
      t,
   });

   const {workflow} = buildWorkflow;
   const isAfterCall = workflow.purpose === "after_call";
   const title = workflow.suggestedName?.trim() || workflow.description;
   const description = workflow.description.trim();
   const showDescription = description.length > 0 && description !== title;
   const isPublished = isAfterCall && (status === "published" || alreadyPublished);

   const progressLabel = progressLabelKey(status, isAfterCall);

   const renderFooter = () => {
      if (progressLabel) {
         return <p className="px-4 pb-3 pt-1 text-xs text-500">{t(progressLabel)}</p>;
      }
      return isAfterCall ? (
         <AfterCallHandoffFooter
            status={status}
            failure={failure}
            linkedWorkflowId={linkedWorkflowId}
            alreadyPublished={alreadyPublished}
            onRetry={() => void startBackgroundBuild()}
            onOpen={openWorkflow}
         />
      ) : (
         <ToolHandoffFooter
            status={status}
            linkedWorkflowId={linkedWorkflowId}
            canApprove={canApprove}
            onApprove={() => void startBuilderHandoff()}
            onOpen={openWorkflow}
         />
      );
   };

   const headerIcon = () => {
      if (status === "error") return "triangle-exclamation";
      if (isPublished) return "circle-check";
      return "diagram-project";
   };

   return (
      <FileBubbleFrame
         isUser={isUser}
         agentName={agentName}
         brandOverride={brandOverride}
         className={className}
         isLastInGroup={isLastInGroup}
         hideAgentHeader={hideAgentHeader}
      >
         <div
            className={clsx(
               "group relative block overflow-hidden rounded-xl border border-300",
               "bg-panel w-full max-w-[620px]"
            )}
         >
            <div className="flex items-center gap-3 px-4 py-3">
               <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                  <Icon icon={headerIcon()} size={ICON_SIZES.SM} />
               </div>
               <div className="flex-1 min-w-0">
                  <p className="font-medium text-900 text-sm truncate" title={title}>
                     {title}
                  </p>
                  <p className="text-[10px] text-500 uppercase tracking-wide mt-0.5">
                     {t("workspace.buildWorkflow.badge")}
                  </p>
               </div>
               {isInProgress && (
                  <div className="border-primary border-b-2 rounded-full w-4 h-4 animate-spin flex-shrink-0" />
               )}
            </div>

            {showDescription && (
               <p className="px-4 -mt-1 pb-1 text-xs text-500 line-clamp-3">{description}</p>
            )}

            {renderFooter()}
         </div>
      </FileBubbleFrame>
   );
};

/** Translation key of the in-progress line, or null when the card is settled. */
function progressLabelKey(status: HandoffStatus, isAfterCall: boolean): string | null {
   switch (status) {
      case "creating":
         return isAfterCall
            ? "workspace.buildWorkflow.creating"
            : "workspace.buildWorkflow.opening";
      case "building":
         return "workspace.buildWorkflow.building";
      case "publishing":
         return "workspace.buildWorkflow.publishing";
      case "navigating":
         return "workspace.buildWorkflow.opening";
      default:
         return null;
   }
}

export default AskWorkflowBuilderBubble;
